/* ============================================================
   TSC ATELIER · Card Holder — Shopify section script
   ────────────────────────────────────────────────────────────
   Vanilla JS port of the React TSCCard component. Auto-boots
   every [data-tsc-card] element on the page (works with any
   Shopify theme, no build step, no dependencies).

   Interactions:
     · click / tap        → toggle flip
     · cursor move        → tilt + holographic foil + specular glare
     · scroll             → diagonal sheen sweeps across the grid
     · CTA click          → navigate to product page (no flip)
     · flip event         → Web-Audio tick + haptic vibrate

   Section boundary: this script only touches elements marked with
   data-tsc-card. No global side-effects.
   ============================================================ */
(function () {
    "use strict";

    var TICK_COOLDOWN_MS = 150;

    // Shared audio context (one per page) — created lazily on first flip
    // so browsers don't complain about un-gestured audio construction.
    var audioCtx = null;
    var lastTickAt = 0;

    function ensureAudio() {
        if (audioCtx) return audioCtx;
        try {
            var Ctx = window.AudioContext || window.webkitAudioContext;
            if (Ctx) audioCtx = new Ctx();
        } catch (e) { /* noop */ }
        return audioCtx;
    }

    function playTick() {
        var now = performance.now();
        if (now - lastTickAt < TICK_COOLDOWN_MS) return;
        lastTickAt = now;
        var ctx = audioCtx;
        if (!ctx) return;
        if (ctx.state === "suspended") {
            try { ctx.resume(); } catch (e) { /* noop */ }
        }
        var t = ctx.currentTime;
        var osc = ctx.createOscillator();
        var gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = 1800;
        gain.gain.setValueAtTime(0.15, t);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.008);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.012);
    }

    function triggerHaptic() {
        if (typeof navigator !== "undefined" && navigator.vibrate) {
            try { navigator.vibrate(8); } catch (e) { /* noop */ }
        }
    }

    function initCard(wrapper) {
        if (wrapper.__tscInit) return; // idempotent
        wrapper.__tscInit = true;

        var inner       = wrapper.querySelector("[data-tsc-inner]");
        var frontHolo   = wrapper.querySelector("[data-tsc-front-holo]");
        var frontGlare  = wrapper.querySelector("[data-tsc-front-glare]");
        var backHolo    = wrapper.querySelector("[data-tsc-back-holo]");
        var backGlare   = wrapper.querySelector("[data-tsc-back-glare]");
        var overlays    = [frontHolo, frontGlare, backHolo, backGlare].filter(Boolean);
        var flipped     = false;

        function setInnerTransform(extraX, extraY) {
            var baseFlip = flipped ? 180 : 0;
            var rx = extraX || 0;
            var ry = (extraY || 0) + baseFlip;
            if (inner) inner.style.transform = "rotateX(" + rx + "deg) rotateY(" + ry + "deg)";
        }

        function applyTilt(clientX, clientY) {
            var rect = wrapper.getBoundingClientRect();
            var x = (clientX - rect.left) / rect.width;
            var y = (clientY - rect.top) / rect.height;
            var rotY = (x - 0.5) * 14;
            var rotX = (0.5 - y) * 10;
            setInnerTransform(rotX, rotY);

            var px = (x * 100) + "%";
            var py = (y * 100) + "%";
            [frontGlare, backGlare].forEach(function (g) {
                if (!g) return;
                g.style.setProperty("--mx", px);
                g.style.setProperty("--my", py);
                g.style.opacity = "1";
            });
            [frontHolo, backHolo].forEach(function (h) {
                if (!h) return;
                h.style.setProperty("--mx", px);
                h.style.setProperty("--my", py);
                h.style.opacity = "0.85";
            });
        }

        function resetVisuals() {
            setInnerTransform(0, 0);
            overlays.forEach(function (o) { o.style.opacity = "0"; });
        }

        function toggleFlip() {
            ensureAudio();
            triggerHaptic();
            playTick();
            flipped = !flipped;
            setInnerTransform(0, 0);
        }

        // ---------- Pointer events ----------
        wrapper.addEventListener("pointerenter", function (e) {
            if (e.pointerType === "mouse" || e.pointerType === "pen") applyTilt(e.clientX, e.clientY);
        });
        wrapper.addEventListener("pointermove", function (e) {
            if (e.pointerType === "mouse" || e.pointerType === "pen") applyTilt(e.clientX, e.clientY);
        });
        wrapper.addEventListener("pointerleave", function (e) {
            if (e.pointerType === "mouse" || e.pointerType === "pen") resetVisuals();
        });

        // ---------- Click / tap toggles flip ----------
        wrapper.addEventListener("click", function (e) {
            // Never flip when the click originated on the CTA anchor.
            if (e.target && e.target.closest && e.target.closest("[data-tsc-cta]")) return;
            toggleFlip();
        });

        // CTA: stop the click / pointer-down from bubbling to the wrapper,
        // but let the anchor's default navigation happen.
        var cta = wrapper.querySelector("[data-tsc-cta]");
        if (cta) {
            cta.addEventListener("click", function (e) { e.stopPropagation(); });
            cta.addEventListener("pointerdown", function (e) { e.stopPropagation(); });
        }
    }

    // ---------- Scroll-driven sheen ----------
    // A single global scroll listener updates the --tsc-sheen CSS variable
    // on every card wrapper. Cards on the left of the viewport lead the
    // sweep; cards on the right trail it, so the highlight feels like
    // one ambient light traveling across the row.
    var scheduled = false;
    function updateSheen() {
        scheduled = false;
        var cards = document.querySelectorAll("[data-tsc-card]");
        if (!cards.length) return;
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var vw = window.innerWidth || document.documentElement.clientWidth;
        for (var i = 0; i < cards.length; i++) {
            var el = cards[i];
            var rect = el.getBoundingClientRect();
            var vProgress = 1 - (rect.top + rect.height / 2) / vh;
            var xNorm = Math.max(0, Math.min(1, (rect.left + rect.width / 2) / vw));
            var stagger = -(xNorm - 0.5) * 0.35;
            var combined = vProgress + stagger;
            var clamped = Math.max(-0.5, Math.min(1.5, combined));
            el.style.setProperty("--tsc-sheen", String(clamped));
        }
    }
    function requestSheenUpdate() {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(updateSheen);
    }

    // ---------- Global listeners (attached exactly once per page) ----------
    // Guarded by a window-level flag so that if Shopify Theme Editor
    // re-executes this script when the section reloads, we do NOT stack
    // duplicate scroll / resize / shopify:section:load handlers.
    function attachGlobalListenersOnce() {
        if (window.__tscCardHolderGlobals) return;
        window.__tscCardHolderGlobals = true;

        window.addEventListener("scroll", requestSheenUpdate, { passive: true });
        window.addEventListener("resize", requestSheenUpdate, { passive: true });

        // Re-init any new cards when the merchant edits the section in the
        // Theme Editor. initCard() itself is idempotent per wrapper.
        document.addEventListener("shopify:section:load", function (e) {
            var scope = e && e.target ? e.target : document;
            var cards = scope.querySelectorAll("[data-tsc-card]");
            for (var i = 0; i < cards.length; i++) initCard(cards[i]);
            updateSheen();
        });
    }

    // ---------- Boot ----------
    function boot() {
        var cards = document.querySelectorAll("[data-tsc-card]");
        for (var i = 0; i < cards.length; i++) initCard(cards[i]);
        updateSheen();
        attachGlobalListenersOnce();
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", boot);
    } else {
        boot();
    }
})();
